import { Button } from '@edsn/pie-components';
import IconDelete from '@iconify/icons-material-symbols/delete-outline';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Icon } from '#pie/components/icon/Icon';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  type CprContactGroupMembership,
  getCprManagementContacts,
  getGetManagementContactQueryKey,
  useDeleteContactGroupMembership,
  useGetCprManagementContacts,
  useGetManagementContact,
} from '#pie/internal/api/pie-bff';

interface RemoveCprContactGroupButtonProps {
  membership: CprContactGroupMembership;
}

export const RemoveCprContactGroupButton = ({
  membership,
}: RemoveCprContactGroupButtonProps) => {
  const { addToast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const { contactPersonId } = useParams()!;
  const { data: contactperson } = useGetManagementContact(contactPersonId!);
  const contactGroupId = membership.contactGroup.id;

  const { data: contactGroups } = useGetCprManagementContacts({
    pagination: { pageIndex: 0, pageSize: 1 },
    filter: { contactGroupIds: [contactGroupId] },
  });

  const requiredGroup = membership.contactGroup.isRequired === true;
  const lastInGroup = (contactGroups?.totalCount ?? 0) === 1;
  const lastInRequiredGroup = requiredGroup && lastInGroup;
  const lastLinkedGroup =
    (contactperson?.contactGroupMemberships.length ?? 0) === 1;
  const isBlocked = lastInRequiredGroup || lastLinkedGroup;

  const { mutate, isPending: isLoading } = useDeleteContactGroupMembership({
    mutation: {
      onError: (error) => {
        addToast({
          message: t('cpr_management_cpr_detail.error.message'),
          title: t('cpr_management_cpr_detail.error.title'),
          type: 'error',
          error,
        });
      },
      onSuccess: () => {
        addToast({
          message: t('cpr_management_cpr_detail.success.message', {
            name: contactperson?.fullName,
          }),
          title: t('cpr_management_cpr_detail.success.title'),
          type: 'success',
        });

        setIsOpen(false);

        queryClient.invalidateQueries({
          queryKey: [getCprManagementContacts.name],
        });

        queryClient.invalidateQueries({
          queryKey: ['/cpr-management-contact'],
        });

        queryClient.refetchQueries({
          queryKey: [getCprManagementContacts.name],
          type: 'active',
        });

        queryClient.invalidateQueries({
          queryKey: getGetManagementContactQueryKey(contactPersonId!),
        });
      },
    },
  });

  return (
    <>
      <Button
        iconStart={<Icon icon={IconDelete} />}
        action="tertiary"
        emphasis="minimal"
        className="ml-auto p-0"
        aria-label={t('cpr_management_group_detail.modal.title.unlink')}
        onClick={() => setIsOpen(true)}
      />
      <Dialog
        title={
          isBlocked
            ? t('cpr_management_group_detail.modal.title.is_blocked')
            : t('cpr_management_contact_detail.modal.title.unlink')
        }
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <Stack gap="lg">
          {lastInRequiredGroup ? (
            <Text>
              {t('cpr_management_contact_detail.modal.message.last_group', {
                user: contactperson?.fullName,
              })}{' '}
              <Link
                to={`/beheer/contactgroepenbeheer/${membership.contactGroup.id}`}
                className="text-content-link"
              >
                {membership.contactGroup.name}
              </Link>
            </Text>
          ) : (
            <Text>
              <Trans
                i18nKey={
                  lastLinkedGroup
                    ? 'cpr_management_contact_detail.modal.message.last_group'
                    : 'cpr_management_contact_detail.modal.message.unlink'
                }
                values={{
                  user: contactperson?.fullName,
                  email: contactperson?.email,
                  contactgroup: membership.contactGroup.name,
                }}
              />
            </Text>
          )}
          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => setIsOpen(false)}
            >
              {isBlocked ? t('common.close') : t('common.button.cancel')}
            </Button>

            {!isBlocked && (
              <Button
                isLoading={isLoading}
                action="danger"
                emphasis="bold"
                onClick={() => {
                  mutate({ id: membership.id });
                }}
              >
                {t('cpr_management_group_detail.button.unlink')}
              </Button>
            )}
          </div>
        </Stack>
      </Dialog>
    </>
  );
};
